
import { ReactNode, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToolUsageLimit } from "@/hooks/useToolUsageLimit";
import { LimitReachedModal } from "@/components/LimitReachedModal";
import { useToolUsageTracker } from "@/hooks/useToolUsageTracker";

interface ToolUsageWrapperProps {
  children: ReactNode;
  toolName: string;
}

export const ToolUsageWrapper = ({ children, toolName }: ToolUsageWrapperProps) => {
  const { incrementUsage, hasReachedLimit, hasReachedToolLimit } = useToolUsageLimit(toolName);
  const { trackToolUsage } = useToolUsageTracker();
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [usageTracked, setUsageTracked] = useState(false);
  const navigate = useNavigate();
  
  // Vérification si l'utilisateur a déjà atteint sa limite
  const hasReachedAnyLimit = hasReachedLimit || hasReachedToolLimit;

  // Effet qui s'exécute une seule fois au chargement de la page
  useEffect(() => {
    // Pour éviter des incrémentations multiples si le composant se re-render
    if (!usageTracked) {
      try {
        // Incrémente le compteur d'utilisation en passant le nom de l'outil
        // Utiliser une action "page-view" spécifique pour ne pas confondre avec les actions de CTA
        const hasReachedLimit = incrementUsage(toolName, "page-view");
        
        // Tente de suivre l'utilisation (fonctionne pour les utilisateurs connectés)
        // Gérer les erreurs silencieusement pour ne pas perturber l'expérience utilisateur
        try {
          trackToolUsage(toolName).catch(err => {
            console.error("Erreur silencieuse lors du suivi d'utilisation:", err);
          });
        } catch (error) {
          console.error("Erreur attrapée lors du tracking:", error);
        }
        
        // Si la limite est atteinte, afficher la modale
        if (hasReachedLimit) {
          setShowLimitModal(true);
        }
      } catch (error) {
        console.error("Erreur lors du tracking d'utilisation:", error);
        // Ne rien faire en cas d'erreur, continuer silencieusement
      }
      
      // Marquer que l'utilisation a été comptabilisée
      setUsageTracked(true);
    }
  }, [toolName, trackToolUsage, incrementUsage, usageTracked]);

  // On NE DOIT PAS retourner conditionnellement ici car cela brise les règles des hooks React
  return (
    <>
      {hasReachedAnyLimit ? (
        <>
          <LimitReachedModal 
            isOpen={true} 
            onClose={() => navigate("/auth")} 
          />
          <div>{children}</div>
        </>
      ) : (
        <>
          <div>{children}</div>
          
          <LimitReachedModal 
            isOpen={showLimitModal} 
            onClose={() => setShowLimitModal(false)} 
          />
        </>
      )}
    </>
  );
};
